import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
  applyRuntimeLocale,
  isInstallableLocale,
  resolveRuntimeLocale,
} from "./localeRegistry";
import {
  BUILT_IN_SOURCE_LOCALE,
  INSTALLABLE_LOCALES,
  LOCALE_DEFINITIONS,
  type InstallableLocale,
} from "./preparedLocales";
import {
  publicSurfaceCatalogHasCriticalCoverage,
  translatePublicSurfaceString,
} from "./publicSurfaceCatalog";
import { LanguagePreferenceControl } from "../ui/LanguagePreferenceControl";
import {
  ThemePreferenceControl,
  type ThemePreferenceLabels,
} from "../ui/ThemePreferenceControl";
import {
  readSharedThemePreference,
  subscribeSharedTheme,
  syncSharedThemePreference,
} from "../ui/theme";

const PUBLIC_LOCALE_STORAGE_KEY = "kavoosh.publicLocale";
const PUBLIC_SURFACE_SELECTOR = ".public-site, .login-page";
const AUTHENTICATED_PREFIXES = [
  "/admin", "/client", "/organization", "/analyst", "/senior", "/setup", "/change-password",
] as const;

type LocalizedSource = { source: string; lastLocalized: string };

export type PublicLocaleAvailability = {
  locale: InstallableLocale;
  language_tag: string;
  direction: "ltr" | "rtl";
  english_label: string;
  installed: boolean;
  enabled: boolean;
  is_default: boolean;
};

const sourceLocale = LOCALE_DEFINITIONS[BUILT_IN_SOURCE_LOCALE];
const ENGLISH_AVAILABILITY: PublicLocaleAvailability = {
  locale: BUILT_IN_SOURCE_LOCALE,
  language_tag: sourceLocale.languageTag,
  direction: sourceLocale.direction,
  english_label: sourceLocale.englishLabel,
  installed: true,
  enabled: true,
  is_default: true,
};

const fallbackLanguage = new WeakMap<Element, { lang: string | null; dir: string | null }>();
const sourceText = new WeakMap<Text, LocalizedSource>();
const sourceAttributes = new WeakMap<Element, Map<string, LocalizedSource>>();
let sourceDocumentTitle: string | null = null;
let lastLocalizedDocumentTitle: string | null = null;

function isPublicPath(path: string): boolean {
  return !AUTHENTICATED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function shouldSkip(element: Element | null): boolean {
  return Boolean(element?.closest("[data-public-foundation-control], [data-kavoosh-technical], .public-policy-page, code, pre, script, style"));
}

function localizeTextNode(node: Text, locale: InstallableLocale): void {
  const parent = node.parentElement;
  if (shouldSkip(parent)) return;
  const current = node.nodeValue ?? "";
  const trimmed = current.trim();
  if (!trimmed) return;
  const previous = sourceText.get(node);
  const source = previous && current === previous.lastLocalized ? previous.source : current;
  const sourceTrimmed = source.trim();
  const translated = translatePublicSurfaceString(locale, sourceTrimmed);
  if (parent && parent.children.length === 0) {
    const fallback = locale !== "en" && translated === sourceTrimmed && (sourceTrimmed.match(/[A-Za-z]{2,}/g)?.length ?? 0) >= 3;
    if (fallback) {
      if (!fallbackLanguage.has(parent)) fallbackLanguage.set(parent, { lang: parent.getAttribute("lang"), dir: parent.getAttribute("dir") });
      parent.setAttribute("lang", "en");
      parent.setAttribute("dir", "ltr");
    } else if (fallbackLanguage.has(parent)) {
      const original = fallbackLanguage.get(parent)!;
      for (const name of ["lang", "dir"] as const) {
        if (original[name] === null) parent.removeAttribute(name);
        else parent.setAttribute(name, original[name]);
      }
      fallbackLanguage.delete(parent);
    }
  }
  const next = source.replace(sourceTrimmed, translated);
  sourceText.set(node, { source, lastLocalized: next });
  if (node.nodeValue !== next) node.nodeValue = next;
}

function localizeAttributes(element: Element, locale: InstallableLocale): void {
  if (shouldSkip(element)) return;
  const states = sourceAttributes.get(element) ?? new Map<string, LocalizedSource>();
  for (const name of ["aria-label", "title", "placeholder", "alt"]) {
    const current = element.getAttribute(name);
    if (!current) continue;
    const previous = states.get(name);
    const source = previous && current === previous.lastLocalized ? previous.source : current;
    const translated = translatePublicSurfaceString(locale, source);
    states.set(name, { source, lastLocalized: translated });
    if (current !== translated) element.setAttribute(name, translated);
  }
  if (states.size) sourceAttributes.set(element, states);
}

function localizeRoot(root: Element, locale: InstallableLocale): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) { localizeTextNode(node as Text, locale); node = walker.nextNode(); }
  localizeAttributes(root, locale);
  root.querySelectorAll("*").forEach((element) => localizeAttributes(element, locale));
  if (root.classList.contains("public-site")) {
    for (const candidate of INSTALLABLE_LOCALES) root.classList.remove(`locale-${candidate}`);
    root.classList.add(`locale-${locale}`);
  }
}

function localizeDocumentTitle(locale: InstallableLocale, path: string): void {
  const current = document.title;
  if (current !== lastLocalizedDocumentTitle) sourceDocumentTitle = current;
  const source = sourceDocumentTitle ?? current;
  let localized = source;
  if (path === "/login") localized = `${translatePublicSurfaceString(locale, "Sign in")} · Kavoosh`;
  else if (source.startsWith("Kavoosh · ")) localized = `Kavoosh · ${translatePublicSurfaceString(locale, source.slice("Kavoosh · ".length))}`;
  else localized = translatePublicSurfaceString(locale, source);
  lastLocalizedDocumentTitle = localized;
  if (document.title !== localized) document.title = localized;
}

async function fetchPublicLocales(signal: AbortSignal): Promise<PublicLocaleAvailability[]> {
  const response = await fetch("/api/v1/locales/public", { signal, credentials: "same-origin", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("public locale inventory unavailable");
  const payload = (await response.json()) as unknown;
  if (!Array.isArray(payload)) throw new Error("public locale inventory is invalid");
  const valid: PublicLocaleAvailability[] = [];
  for (const item of payload) {
    if (!item || typeof item !== "object") continue;
    const value = item as Record<string, unknown>;
    if (!isInstallableLocale(value.locale) || typeof value.language_tag !== "string" || (value.direction !== "ltr" && value.direction !== "rtl") || typeof value.english_label !== "string" || typeof value.installed !== "boolean" || typeof value.enabled !== "boolean" || typeof value.is_default !== "boolean") continue;
    const definition = LOCALE_DEFINITIONS[value.locale];
    if (value.language_tag !== definition.languageTag || value.direction !== definition.direction || value.english_label !== definition.englishLabel) continue;
    valid.push(value as PublicLocaleAvailability);
  }
  if (!valid.some(
    (item) => item.locale === BUILT_IN_SOURCE_LOCALE && item.enabled && item.installed,
  )) throw new Error("built-in source locale is unavailable");
  return valid;
}

function readStoredPublicLocale(): string | null {
  try { return window.localStorage.getItem(PUBLIC_LOCALE_STORAGE_KEY); } catch { return null; }
}
function writeStoredPublicLocale(locale: InstallableLocale): void {
  try { window.localStorage.setItem(PUBLIC_LOCALE_STORAGE_KEY, locale); } catch { /* viewer convenience only */ }
}

export function PublicSurfaceFoundation({ path }: { path: string }) {
  const active = isPublicPath(path);
  const [inventory, setInventory] = useState<PublicLocaleAvailability[]>([ENGLISH_AVAILABILITY]);
  const [locale, setLocale] = useState<InstallableLocale>(BUILT_IN_SOURCE_LOCALE);
  const [host, setHost] = useState<HTMLElement | null>(null);

  const enabledLocales = useMemo(() => inventory.filter((item) => item.installed && item.enabled && publicSurfaceCatalogHasCriticalCoverage(item.locale)).map((item) => item.locale), [inventory]);

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    void fetchPublicLocales(controller.signal).then(setInventory).catch(() => setInventory([ENGLISH_AVAILABILITY]));
    return () => controller.abort();
  }, [active]);

  useEffect(() => {
    if (!active) return;
    setLocale(resolveRuntimeLocale({ enabledLocales, explicitLocale: readStoredPublicLocale(), browserLanguages: navigator.languages }).locale);
  }, [active, enabledLocales]);

  useEffect(() => {
    if (!active) return;
    applyRuntimeLocale(locale);
    const apply = () => {
      document.querySelectorAll(PUBLIC_SURFACE_SELECTOR).forEach((root) => localizeRoot(root, locale));
      localizeDocumentTitle(locale, path);
    };
    apply();
    const bodyObserver = new MutationObserver(apply);
    bodyObserver.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["aria-label", "title", "placeholder", "alt"] });
    const headObserver = new MutationObserver(apply);
    headObserver.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => { bodyObserver.disconnect(); headObserver.disconnect(); };
  }, [active, locale, path]);

  useEffect(() => {
    if (!active) return;
    const current = readSharedThemePreference();
    syncSharedThemePreference(current);
    const unsubscribe = subscribeSharedTheme((next) => {
      syncSharedThemePreference(next);
    });
    if (typeof window.matchMedia !== "function") return unsubscribe;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const syncSystem = () => {
      if (readSharedThemePreference() === "system") syncSharedThemePreference("system");
    };
    media.addEventListener?.("change", syncSystem);
    return () => { unsubscribe(); media.removeEventListener?.("change", syncSystem); };
  }, [active]);

  useEffect(() => {
    if (!active) { setHost(null); return; }
    const updateHost = () => setHost(document.querySelector<HTMLElement>(path === "/login" ? ".login-top-actions" : ".public-site .header-actions"));
    updateHost();
    const observer = new MutationObserver(updateHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [active, path]);

  if (!active || !host) return null;
  const t = (source: string) => translatePublicSurfaceString(locale, source);
  const themeLabels: ThemePreferenceLabels = {
    appearance: t("Theme"),
    system: t("System"),
    light: t("Light"),
    dark: t("Dark"),
  };
  return createPortal(
    <div
      className={`public-foundation-controls ${path === "/login" ? "auth" : "landing"}`}
      data-public-foundation-control="true"
      dir={LOCALE_DEFINITIONS[locale].direction}
    >
      <LanguagePreferenceControl
        activeLocale={locale}
        locales={enabledLocales}
        label={t("Language")}
        onSelect={(next) => {
          if (!enabledLocales.includes(next)) return;
          writeStoredPublicLocale(next);
          setLocale(next);
        }}
      />
      <ThemePreferenceControl labels={themeLabels} />
    </div>,
    host,
  );
}
